import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import {
  $createLineBreakNode,
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $isRootNode,
  COMMAND_PRIORITY_HIGH,
  ElementNode,
  EditorState,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_UP_COMMAND,
  KEY_ENTER_COMMAND,
  KEY_TAB_COMMAND,
  LexicalEditor,
  LexicalNode,
  TextNode,
} from "lexical";
import {
  MentionedAsset,
  MENTION_REGEX,
  MENTION_TERMINATOR,
} from "../hooks/useAtMention";
import { $createMentionNode, MentionNode } from "../lexical/MentionNode";

// Keys the mention picker may consume while it is open.
export type PickerKey = "Enter" | "Tab" | "ArrowUp" | "ArrowDown";

export interface MentionEditorHandle {
  focus: () => void;
  // Replace the "@query" run before the caret with an atomic mention pill + trailing space.
  insertMention: (entity: MentionedAsset) => void;
}

interface MentionEditorProps {
  // Canonical string value: plain text with `@{label}​` mention tokens inline.
  value: string;
  onChange: (value: string, caretOffset: number) => void;
  // Enter (without shift) sends when provided; when absent Enter inserts a newline (schedule prompts are multi-line documents, not messages).
  onSend?: () => void;
  // Return true while the picker owns the key — the editor preventDefaults and the picker's window-level keydown listener performs the actual action.
  onPickerKey?: (key: PickerKey) => boolean;
  // Label → entity lookup used to rebuild pills when the value is set from outside (drafts, suggested questions, saved schedule prompts).
  resolveEntity: (label: string) => MentionedAsset | undefined;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

// Rebuild the editor document from a canonical string: text runs and line breaks, with mention tokens becoming atomic MentionNodes. Unknown labels get a fallback entity so the pill still renders (and serializes back to the identical token).
function $populateFromValue(
  value: string,
  resolveEntity: (label: string) => MentionedAsset | undefined,
  selectEnd = true
): void {
  const root = $getRoot();
  root.clear();
  const paragraph = $createParagraphNode();
  const appendText = (text: string) => {
    const lines = text.split("\n");
    lines.forEach((line, i) => {
      if (i > 0) paragraph.append($createLineBreakNode());
      if (line) paragraph.append($createTextNode(line));
    });
  };
  const regex = new RegExp(MENTION_REGEX.source, MENTION_REGEX.flags);
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(value)) !== null) {
    if (match.index > lastIndex) appendText(value.slice(lastIndex, match.index));
    const label = match[0].slice(1).split(MENTION_TERMINATOR).join("");
    const entity = resolveEntity(label) ?? { id: label, name: label };
    paragraph.append($createMentionNode(entity));
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < value.length) appendText(value.slice(lastIndex));
  root.append(paragraph);
  // Skipped for the initial state — a selection there makes Lexical focus the field on mount, popping the phone keyboard
  if (selectEnd) paragraph.selectEnd();
}

// Absolute caret offset within the serialized text. MentionNodes count as their full token length, so offsets line up with getMentionQueryAtCursor's math on the canonical string.
function $caretOffset(): number {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return $getRoot().getTextContentSize();
  const anchor = selection.anchor;
  const anchorNode = anchor.getNode();
  let offset = 0;
  if (anchor.type === "text") {
    offset = anchor.offset;
  } else {
    const children = (anchorNode as ElementNode).getChildren();
    for (let i = 0; i < Math.min(anchor.offset, children.length); i++) {
      offset += children[i].getTextContentSize();
    }
  }
  let node: LexicalNode | null = anchorNode;
  while (node !== null && !$isRootNode(node)) {
    let sibling: LexicalNode | null = node.getPreviousSibling();
    while (sibling !== null) {
      offset += sibling.getTextContentSize();
      sibling = sibling.getPreviousSibling();
    }
    node = node.getParent();
  }
  return offset;
}

// Captures the editor instance for the imperative handle without an extra dependency.
const EditorCapturePlugin: React.FC<{ editorRef: React.MutableRefObject<LexicalEditor | null> }> = ({
  editorRef,
}) => {
  const [editor] = useLexicalComposerContext();
  editorRef.current = editor;
  return null;
};

// Repopulates the document whenever the value prop changes from outside the editor (draft restore, suggested question, /command prefill, send-clears-input).
const ValueSyncPlugin: React.FC<{
  value: string;
  lastEmittedRef: React.MutableRefObject<{ text: string; caret: number }>;
  resolveEntityRef: React.MutableRefObject<(label: string) => MentionedAsset | undefined>;
}> = ({ value, lastEmittedRef, resolveEntityRef }) => {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    if (value === lastEmittedRef.current.text) return;
    lastEmittedRef.current = { text: value, caret: value.length };
    // Only keep the caret at the end if the field already has focus — an outside sync (draft restore) shouldn't focus it and pop the phone keyboard
    const hasFocus = editor.getRootElement() === document.activeElement;
    editor.update(() => {
      $populateFromValue(value, resolveEntityRef.current, hasFocus);
    });
  }, [value, editor, lastEmittedRef, resolveEntityRef]);
  return null;
};

// Routes Enter/Tab/arrows through the picker first; Enter (no shift) then sends.
const KeysPlugin: React.FC<{
  onPickerKeyRef: React.MutableRefObject<((key: PickerKey) => boolean) | undefined>;
  onSendRef: React.MutableRefObject<(() => void) | undefined>;
}> = ({ onPickerKeyRef, onSendRef }) => {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    const pickerConsumes = (key: PickerKey, event: KeyboardEvent | null): boolean => {
      if (onPickerKeyRef.current?.(key)) {
        // preventDefault only — the event still bubbles to the picker's window-level keydown listener, which performs the navigation/selection.
        event?.preventDefault();
        return true;
      }
      return false;
    };
    const unregisterFns = [
      editor.registerCommand<KeyboardEvent | null>(
        KEY_ENTER_COMMAND,
        (event) => {
          if (pickerConsumes("Enter", event)) return true;
          if (event && !event.shiftKey && onSendRef.current) {
            event.preventDefault();
            onSendRef.current();
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand<KeyboardEvent>(
        KEY_TAB_COMMAND,
        (event) => pickerConsumes("Tab", event),
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand<KeyboardEvent>(
        KEY_ARROW_UP_COMMAND,
        (event) => pickerConsumes("ArrowUp", event),
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand<KeyboardEvent>(
        KEY_ARROW_DOWN_COMMAND,
        (event) => pickerConsumes("ArrowDown", event),
        COMMAND_PRIORITY_HIGH
      ),
    ];
    return () => unregisterFns.forEach((unregister) => unregister());
  }, [editor, onPickerKeyRef, onSendRef]);
  return null;
};

const MentionEditor = forwardRef<MentionEditorHandle, MentionEditorProps>(
  ({ value, onChange, onSend, onPickerKey, resolveEntity, placeholder, disabled = false, className = "" }, ref) => {
    const editorRef = useRef<LexicalEditor | null>(null);
    // Last serialization emitted to (or received from) the parent — the loop-breaker between OnChange emissions and ValueSync repopulations.
    const lastEmittedRef = useRef({ text: value, caret: value.length });

    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const onSendRef = useRef(onSend);
    onSendRef.current = onSend;
    const onPickerKeyRef = useRef(onPickerKey);
    onPickerKeyRef.current = onPickerKey;
    const resolveEntityRef = useRef(resolveEntity);
    resolveEntityRef.current = resolveEntity;

    const initialValueRef = useRef(value);
    const initialConfig = useRef({
      namespace: "MentionEditor",
      nodes: [MentionNode],
      editable: !disabled,
      onError: (error: Error) => {
        console.error("[MentionEditor]", error);
      },
      editorState: () => $populateFromValue(initialValueRef.current, resolveEntityRef.current, false),
    }).current;

    useEffect(() => {
      editorRef.current?.setEditable(!disabled);
    }, [disabled]);

    useImperativeHandle(
      ref,
      () => ({
        focus: () => editorRef.current?.focus(),
        insertMention: (entity: MentionedAsset) => {
          const editor = editorRef.current;
          if (!editor) return;
          editor.update(() => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection)) return;
            const mentionNode = $createMentionNode(entity);
            const spaceNode = $createTextNode(" ");
            if (selection.isCollapsed() && selection.anchor.type === "text") {
              const anchorNode = selection.anchor.getNode();
              if (anchorNode instanceof TextNode) {
                const cursor = selection.anchor.offset;
                const atIndex = anchorNode.getTextContent().slice(0, cursor).lastIndexOf("@");
                if (atIndex !== -1) {
                  // Split off the "@query" run and replace it with the pill.
                  let target: TextNode = anchorNode;
                  if (atIndex > 0) {
                    const parts = anchorNode.splitText(atIndex);
                    target = parts[1] ?? parts[0];
                  }
                  const queryLen = cursor - atIndex;
                  if (target.getTextContentSize() > queryLen) {
                    target = target.splitText(queryLen)[0];
                  }
                  target.replace(mentionNode);
                  mentionNode.insertAfter(spaceNode);
                  spaceNode.select(1, 1);
                  return;
                }
              }
            }
            // No @query at the caret — insert the pill at the selection as-is.
            selection.insertNodes([mentionNode, spaceNode]);
            spaceNode.select(1, 1);
          });
        },
      }),
      []
    );

    const handleEditorChange = (editorState: EditorState) => {
      editorState.read(() => {
        const text = $getRoot().getTextContent();
        const caret = $caretOffset();
        if (text === lastEmittedRef.current.text && caret === lastEmittedRef.current.caret) return;
        lastEmittedRef.current = { text, caret };
        onChangeRef.current(text, caret);
      });
    };

    return (
      <LexicalComposer initialConfig={initialConfig}>
        <div className="relative flex-1">
          <PlainTextPlugin
            contentEditable={
              <ContentEditable
                className={`bg-transparent text-eai-body tracking-[-0.16px] text-eai-ink-subtle focus:outline-none overflow-y-auto whitespace-pre-wrap break-words ${className} ${
                  disabled ? "cursor-not-allowed opacity-50" : ""
                }`}
              />
            }
            placeholder={
              <div className="pointer-events-none absolute left-0 top-0 text-eai-body tracking-[-0.16px] text-eai-ink-subtle">
                {placeholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <OnChangePlugin onChange={handleEditorChange} />
          <EditorCapturePlugin editorRef={editorRef} />
          <ValueSyncPlugin value={value} lastEmittedRef={lastEmittedRef} resolveEntityRef={resolveEntityRef} />
          <KeysPlugin onPickerKeyRef={onPickerKeyRef} onSendRef={onSendRef} />
        </div>
      </LexicalComposer>
    );
  }
);

MentionEditor.displayName = "MentionEditor";

export default MentionEditor;
